import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { ArrowRight, Zap, Target, Shield } from 'lucide-react';

const KEY_MESSAGES = [
  {
    icon: Zap,
    key: 'speed',
    titleKey: 'landing.keyMessages.speed.title',
    descKey: 'landing.keyMessages.speed.desc',
    defaultTitle: 'Speed',
    defaultDesc: 'Create RFPs 60-80% faster with AI',
  },
  {
    icon: Target,
    key: 'specialization',
    titleKey: 'landing.keyMessages.specialization.title',
    descKey: 'landing.keyMessages.specialization.desc',
    defaultTitle: 'Specialization',
    defaultDesc: 'AI-matched vendors in seconds',
  },
  {
    icon: Shield,
    key: 'certainty',
    titleKey: 'landing.keyMessages.certainty.title',
    descKey: 'landing.keyMessages.certainty.desc',
    defaultTitle: 'Certainty',
    defaultDesc: 'PIPEDA-compliant & accessible',
  },
];

export default function SimpleLandingHero({ onBusinessClick, onVendorClick }) {
  const { t } = useTranslation();

  return (
    <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto text-center">

        {/* ── Headline ────────────────────────────────────────── */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
          {t('landing.simpleHero.title') || 'The Canadian marketplace for smarter procurement'}
        </h1>

        <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
          {t('landing.simpleHero.subtitle') ||
           'Connect businesses with qualified vendors. AI-powered, bilingual, built for Canadian procurement.'}
        </p>

        {/* ── Dual CTAs ───────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <Button
            size="lg"
            onClick={onBusinessClick}
            className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 group"
            data-testid="cta-build-rfp"
          >
            {t('landing.simpleHero.ctaBusiness') || 'Build Your RFP'}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>

          <Button
            size="lg"
            onClick={onVendorClick}
            variant="outline"
            className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground text-lg px-8 py-6 group"
            data-testid="cta-list-services"
          >
            {t('landing.simpleHero.ctaVendor') || 'List Your Services'}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        {/* ── 3 Key Messages ──────────────────────────────────── */}
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {KEY_MESSAGES.map(({ icon: Icon, key, titleKey, descKey, defaultTitle, defaultDesc }) => (
            <div key={key} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary text-primary mb-3">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {t(titleKey) || defaultTitle}
              </h3>
              <p className="text-sm text-slate-600">
                {t(descKey) || defaultDesc}
              </p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
